/**
 * <CierreMensualCard> — Control DESTACADO de publicación mensual al cliente.
 *
 * La compuerta única de visibilidad del cliente (`cierres_cliente`) publica u
 * oculta el MES COMPLETO de una propiedad: arriendos, gastos/flujos, aseos,
 * lavandería, productos y estadísticas. Antes este control era un botón gris
 * más de la barra de `/arriendos`, lo que escondía su alcance real (no es solo
 * de arriendos) y su importancia. Ahora vive prominente arriba de
 * `/finanzas-empresa` como una tarjeta con estado del mes + CTA claro.
 *
 * - Estado en vivo del mes seleccionado (X/Y propiedades visibles al cliente),
 *   calculado con la MISMA regla del backend (explícito gana; sin fila →
 *   visible si el mes es histórico, oculto si es el mes en curso/futuro).
 * - El modal publica/oculta por mes + propiedad opcional (vacío = todas).
 *
 * Ver docs/flujo-financiero.md §13.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, DatePicker, Form, Modal, Select, Spin, Tag } from "antd";
import { SendOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { finanzasEmpresaApi } from "../../services/finanzasEmpresa.api";
import { formatPropertyName } from "../../utils/propertyDisplay";
import { getMsg } from "../../utils/antdStatic";
import { CierreDetalleModal } from "./CierreDetalleModal";
import { ahoraChile } from "../../utils/fechaChile";

interface CierreMensualCardProps {
  propiedades: { id: number; nombre: string }[];
  /** Mes preseleccionado (viene del filtro global de la página). */
  defaultMonth?: Dayjs;
}

export function CierreMensualCard({
  propiedades,
  defaultMonth,
}: CierreMensualCardProps) {
  const [open, setOpen] = useState(false);
  const [detalleOpen, setDetalleOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm<{
    mes?: Dayjs | null;
    idPropiedad?: number | null;
    visible: boolean;
  }>();

  // Mes cuyo estado muestra la tarjeta = el mes del filtro global de la página.
  const statusMonth = defaultMonth ?? ahoraChile();
  const statusMes = statusMonth.format("YYYY-MM");
  const [rows, setRows] = useState<any[]>([]);
  const [statusLoading, setStatusLoading] = useState(false);
  // Bump para recargar el estado tras publicar/ocultar.
  const [reloadKey, setReloadKey] = useState(0);

  const loadStatus = useCallback(async (mes: string) => {
    setStatusLoading(true);
    const res = await finanzasEmpresaApi.getCierresMes(mes);
    setRows(Array.isArray(res?.rows) ? (res as any).rows : []);
    setStatusLoading(false);
  }, []);

  useEffect(() => {
    void loadStatus(statusMes);
  }, [statusMes, reloadKey, loadStatus]);

  // Estado EFECTIVO del mes: espeja la regla de `publishedPropIdsForPeriodo`
  // del backend — la fila explícita gana; sin fila, el mes histórico (<mes
  // actual) es visible y el mes en curso/futuro está oculto.
  const status = useMemo(() => {
    const explicit = new Map<number, boolean>(
      rows.map((r) => [Number(r.id_propiedad), Boolean(r.publicado)]),
    );
    const esHistorico = statusMes < ahoraChile().format("YYYY-MM");
    let visibles = 0;
    for (const p of propiedades) {
      const v = explicit.has(p.id) ? Boolean(explicit.get(p.id)) : esHistorico;
      if (v) visibles++;
    }
    return { total: propiedades.length, visibles, esHistorico };
  }, [rows, propiedades, statusMes]);

  const statusTag = useMemo(() => {
    if (statusLoading) return <Spin size="small" />;
    const { total, visibles } = status;
    if (total === 0) return <Tag>Sin propiedades</Tag>;
    if (visibles === 0)
      return <Tag color="red">Sin publicar · el cliente no ve nada</Tag>;
    if (visibles === total)
      return <Tag color="green">Publicado · el cliente ve todo</Tag>;
    return (
      <Tag color="orange">
        Parcial · {visibles}/{total} propiedades visibles
      </Tag>
    );
  }, [status, statusLoading]);

  const openModal = () => {
    form.setFieldsValue({
      mes: defaultMonth ?? ahoraChile(),
      idPropiedad: null,
      visible: true,
    });
    setOpen(true);
  };

  const submit = async () => {
    try {
      const vals = await form.validateFields();
      if (!vals.mes) {
        getMsg()?.warning("Selecciona el mes a publicar/ocultar.");
        return;
      }
      const body: { publicado: boolean; mes: string; idPropiedad?: number } = {
        publicado: Boolean(vals.visible),
        mes: dayjs(vals.mes).format("YYYY-MM"),
      };
      if (vals.idPropiedad) body.idPropiedad = Number(vals.idPropiedad);
      setLoading(true);
      const res = await finanzasEmpresaApi.setCierreMensual(body);
      if (res?.data?.affected != null) {
        // `notificados` = clientes a los que se les envía el correo de cierre.
        // Solo cuenta las propiedades que pasaron de ocultas a publicadas, así
        // que republicar un mes ya publicado muestra 0 — es correcto, no un
        // fallo. Round 2026-07-27.
        const avisados = Number(res.data.notificados) || 0;
        // Round 2026-07-28: un cero puede venir de tres sitios distintos y sin
        // decirlo son indistinguibles — no había a quién escribir, el dueño
        // apagó los avisos en Configuración, o esas propiedades los tienen
        // apagados en su ficha. Lo decimos.
        const silenciadas = Number(res.data.propiedadesSilenciadas) || 0;
        const detalleCorreo = res.data.correosDesactivados
          ? " No se envió aviso por correo: están desactivados en Configuración → Correos al cliente."
          : avisados
            ? ` Se está enviando el aviso por correo a ${avisados} cliente(s).`
            : silenciadas
              ? ` No se envió aviso: ${silenciadas} propiedad(es) tienen los correos apagados en su ficha.`
              : "";
        getMsg()?.success(
          `${res.data.affected} propiedad(es) — mes ${body.mes} ${
            body.publicado ? "publicado al cliente" : "ocultado al cliente"
          }.${detalleCorreo}`,
        );
      }
      setOpen(false);
      form.resetFields();
      // Refresca el estado de la tarjeta (si publicaste el mismo mes que muestra).
      setReloadKey((k) => k + 1);
    } catch (err: any) {
      if (err?.errorFields) return; // validación: silencioso
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="cierre-card">
        <div className="cierre-card__icon" aria-hidden>
          <SendOutlined />
        </div>
        <div className="cierre-card__body">
          <div className="cierre-card__title">Publicación al cliente</div>
          <div className="cierre-card__subtitle">
            Publica el <strong>mes completo</strong> en el panel del cliente:
            arriendos, gastos, aseos, lavandería, productos y estadísticas. Es la{" "}
            <strong>única</strong> compuerta que le da acceso a la información
            financiera del mes.
          </div>
          <div className="cierre-card__status">
            <span className="cierre-card__status-mes">
              {statusMonth.format("MMMM YYYY")}
            </span>
            {statusTag}
            <button
              type="button"
              className="cierre-card__detalle"
              onClick={() => setDetalleOpen(true)}
            >
              Ver detalle por propiedad y mes
            </button>
          </div>
        </div>
        <div className="cierre-card__action">
          <Button
            size="large"
            icon={<SendOutlined />}
            onClick={openModal}
            className="cierre-card__btn"
          >
            Publicar / cerrar mes…
          </Button>
        </div>
      </div>

      <Modal
        open={open}
        title="Publicar / cerrar el mes al cliente"
        okText="Aplicar"
        cancelText="Cancelar"
        confirmLoading={loading}
        onCancel={() => setOpen(false)}
        onOk={submit}
        centered
      >
        <div
          style={{
            background: "#f6f0ff",
            border: "1px solid #d3adf7",
            padding: 12,
            borderRadius: 8,
            marginBottom: 14,
            fontSize: 13,
          }}
        >
          <strong>Publicar</strong> revela al cliente <strong>todo</strong> el
          mes elegido de la propiedad (arriendos, gastos/flujos, aseos,
          lavandería, productos y estadísticas) en <code>/finanzas-cliente</code>
          . <strong>Ocultar</strong> lo vuelve invisible. Si no eliges propiedad,
          aplica a <strong>todas</strong>. Los meses anteriores al actual ya
          están visibles por defecto.
        </div>
        {/* El correo sale solo al publicar y solo a quien no lo tenía ya
            publicado, así que republicar para corregir algo no reenvía nada.
            Vale decirlo acá: el admin no puede deshacer un correo. */}
        <div
          style={{
            background: "#f0f9f4",
            border: "1px solid #b7eb8f",
            padding: 12,
            borderRadius: 8,
            marginBottom: 14,
            fontSize: 13,
          }}
        >
          Al <strong>publicar</strong> se le envía un correo a cada cliente
          avisándole que su cierre está listo, con sus observaciones pendientes
          si las tiene. Solo se avisa de las propiedades que pasan de ocultas a
          publicadas: volver a publicar un mes ya publicado{" "}
          <strong>no reenvía</strong> el correo.
        </div>
        <Form form={form} layout="vertical" initialValues={{ visible: true }}>
          <Form.Item
            label="Mes a publicar/ocultar"
            name="mes"
            rules={[{ required: true, message: "Selecciona el mes" }]}
            tooltip="Mes contable (check-in para arriendos; fecha del movimiento para el resto)."
          >
            <DatePicker
              picker="month"
              format="YYYY-MM"
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item
            label="Propiedad (opcional)"
            name="idPropiedad"
            tooltip="Si vacío, aplica a TODAS las propiedades para ese mes."
          >
            <Select
              placeholder="Todas las propiedades"
              allowClear
              showSearch
              optionFilterProp="label"
              options={propiedades.map((p) => ({
                value: p.id,
                label: formatPropertyName(p.nombre),
              }))}
            />
          </Form.Item>
          <Form.Item
            label="Acción"
            name="visible"
            tooltip="Publicar = el cliente ve el mes. Ocultar = lo deja invisible."
          >
            <Select
              options={[
                { value: true, label: "Publicar (el cliente ve el mes)" },
                {
                  value: false,
                  label: "Ocultar (el cliente deja de ver el mes)",
                },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      <CierreDetalleModal
        open={detalleOpen}
        onClose={() => setDetalleOpen(false)}
        propiedades={propiedades}
        anchorMonth={statusMonth}
      />
    </>
  );
}
